import React from "react";
import { Link } from "react-router-dom";
import { ShieldOff, KeyRound, Server, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CopyBlock } from "@/components/CopyBlock";
import {
  YOU_RECEIVE,
  YOU_GENERATE,
  NEVER_COPY,
  generateSecretsCommands,
  ghcrLoginCommands,
  composeInstallCommands,
  k8sCommands,
  anyRuntimeCommands,
  moveServerCommands,
  afterInstallNotes,
  IMAGES,
} from "@/lib/deployGuide";

export default function DeployGuide() {
  const pack = {};

  return (
    <div data-testid="deploy-guide-page">
      <section className="border-b border-border">
        <div className="mx-auto max-w-3xl px-6 py-16 lg:px-8 lg:py-20">
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            Customer deploy handbook
          </p>
          <h1 className="font-display text-4xl font-extrabold tracking-tight sm:text-5xl">
            Run GovernX on your servers
          </h1>
          <p className="mt-5 text-lg text-muted-foreground">
            Any Linux VM, Kubernetes cluster, or OCI runtime. You pull private images.
            You never receive source, and you never copy keys from Pravadh HQ.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/signup">
              <Button className="font-semibold" data-testid="deploy-guide-cta">
                Start a trial <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/features" className="text-sm font-semibold text-foreground underline-offset-4 hover:underline">
              Why images-only
            </Link>
          </div>
        </div>
      </section>

      <section className="border-b border-border py-14" data-testid="deploy-guide-rules">
        <div className="mx-auto grid max-w-5xl gap-5 px-6 md:grid-cols-3 lg:px-8">
          <RuleCard
            icon={Server}
            title="You receive"
            items={YOU_RECEIVE}
            testid="deploy-guide-receive"
          />
          <RuleCard
            icon={KeyRound}
            title="You generate on the box"
            items={YOU_GENERATE}
            testid="deploy-guide-generate"
          />
          <RuleCard
            icon={ShieldOff}
            title="Never copy"
            items={NEVER_COPY}
            testid="deploy-guide-never"
          />
        </div>
      </section>

      <section className="py-14">
        <div className="mx-auto max-w-3xl space-y-10 px-6 lg:px-8">
          <Step n="1" title="License pack from Pravadh">
            <p>
              Superadmin at HQ opens Admin → Deployments, registers your server (label, email, domain),
              and sends you three values shown once: INSTANCE_ID, INSTALL_TOKEN, LICENSE_SECRET — plus a
              GitHub PAT with <strong>read:packages</strong> only. That is the entire pack. No git clone.
            </p>
            <p className="mt-3 text-sm text-muted-foreground">
              Images: <code>{IMAGES.backend}</code>, <code>{IMAGES.frontend}</code>, <code>{IMAGES.agent}</code>
              {" "}tagged <code>stable</code> (or a sha HQ pushed). Mongo is public <code>{IMAGES.mongo}</code>.
            </p>
          </Step>

          <Step n="2" title="Generate instance keys — never reuse HQ">
            <p className="mb-3">
              JWT, Fernet, and first-login passwords must be created on the new server. Do not paste
              Razorpay, Resend, Anthropic, or JWT values from any other GovernX environment.
            </p>
            <CopyBlock text={generateSecretsCommands()} testid="deploy-guide-secrets" />
          </Step>

          <Step n="3" title="Log in to the private registry">
            <CopyBlock text={ghcrLoginCommands()} testid="deploy-guide-ghcr" />
          </Step>

          <Step n="4" title="Pick how this server runs">
            <p className="mb-4">
              Same images everywhere. Compose is the fastest one-box path. Kubernetes and raw OCI
              use your own reverse proxy: <code>/api</code> to backend :8000, everything else to frontend :80.
            </p>
            <Tabs defaultValue="compose" data-testid="deploy-guide-runtimes">
              <TabsList className="flex h-auto flex-wrap">
                <TabsTrigger value="compose" data-testid="deploy-tab-compose">Linux / Compose</TabsTrigger>
                <TabsTrigger value="k8s" data-testid="deploy-tab-k8s">Kubernetes</TabsTrigger>
                <TabsTrigger value="oci" data-testid="deploy-tab-oci">Any OCI runtime</TabsTrigger>
                <TabsTrigger value="move" data-testid="deploy-tab-move">Move existing</TabsTrigger>
              </TabsList>
              <TabsContent value="compose" className="mt-4">
                <CopyBlock text={composeInstallCommands(pack)} testid="deploy-guide-compose" />
              </TabsContent>
              <TabsContent value="k8s" className="mt-4">
                <CopyBlock text={k8sCommands(pack)} testid="deploy-guide-k8s" />
              </TabsContent>
              <TabsContent value="oci" className="mt-4">
                <CopyBlock text={anyRuntimeCommands(pack)} testid="deploy-guide-oci" />
              </TabsContent>
              <TabsContent value="move" className="mt-4">
                <CopyBlock text={moveServerCommands()} testid="deploy-guide-move" />
              </TabsContent>
            </Tabs>
          </Step>

          <Step n="5" title="DNS, TLS, brand, your LLM">
            <CopyBlock text={afterInstallNotes(pack)} testid="deploy-guide-after" />
            <p className="mt-3 text-sm text-muted-foreground">
              Subscription dates and lock/unlock stay at Pravadh. If HQ revokes the license, this
              instance becomes read-only. Updates: HQ sets a tag; Compose/agent pull, or you roll
              the Kubernetes image.
            </p>
          </Step>
        </div>
      </section>
    </div>
  );
}

function RuleCard({ icon: Icon, title, items, testid }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={testid}>
      <div className="mb-3 flex items-center gap-2">
        <Icon className="h-4 w-4 text-primary" />
        <h2 className="font-display text-base font-bold">{title}</h2>
      </div>
      <ul className="list-disc space-y-2 pl-4 text-sm text-muted-foreground">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <div>
      <h2 className="font-display text-xl font-bold tracking-tight">
        <span className="mr-2 text-primary">{n}.</span>
        {title}
      </h2>
      <div className="mt-3 text-sm leading-relaxed text-foreground">{children}</div>
    </div>
  );
}
